import React, { useCallback, useMemo, useRef } from 'react';

import { Box, IconVariant, VStack } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { TWELVE_HOUR_TIME_PARSE_FORMAT } from 'features/store/constants';
import { useIsMobileOrderingAvailable, useSelectStore } from 'features/store/hooks';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { NonTraditionalStoreDialogDrawer } from 'features/store/pages/store-locator/components/non-traditional-store-dialog-drawer';
import { useNonTraditionalStoreUI } from 'features/store/pages/store-locator/hooks/use-non-traditional-store';
import {
  getStoreLocalTime,
  getUSState,
  mergeRestaurantData,
  readableTimeInterval,
} from 'features/store/utils';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import CloseButton from 'components/close-button';
import { Helmet } from 'components/helmet';
import Picture from 'components/picture';
import StaticMap from 'components/static-map';
import { ServiceMode, useGetRestaurantQuery } from 'generated/graphql-gateway';
import { IImage, useGetRestaurantsQuery } from 'generated/sanity-graphql';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useErrorModal from 'hooks/use-error-modal';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { primitive } from 'styles/constants/primitives';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { routes } from 'utils/routing';
import { getStoreSEOSlug } from 'utils/slugify';

import { DirectionsLink } from './directions-link';
import StoreMetaForSEO from './meta';
import {
  Address,
  Amenity,
  AmenityIcon,
  FeatureIcon,
  HeaderContainer,
  InnerHeader,
  InnerText,
  PhoneNumber,
  SpacingLine,
  StoreHourContainer,
} from './styled';
import { IHoursProps, IStoreInfoModalProps } from './types';

const Hours = ({ title, hours }: IHoursProps) => {
  const { formatMessage } = useIntl();
  const timeFormatConfig = useConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  const hoursOfOperation = readableTimeInterval({
    fallbackMessage: formatMessage({ id: 'hoursOfOperationFallbackMessage' }),
    open24HoursMessage: formatMessage({ id: 'open24Hours' }),
    timeFormat: timeFormatConfig,
  });

  const storeHoursSummary: [string, string][] = [
    ['monday', 'mon'],
    ['tuesday', 'tue'],
    ['wednesday', 'wed'],
    ['thursday', 'thr'],
    ['friday', 'fri'],
    ['saturday', 'sat'],
    ['sunday', 'sun'],
  ];

  if (!hours) {
    return null;
  }

  return (
    <Box>
      <InnerHeader variant="headerThree" marginY="$2">
        {title}
      </InnerHeader>
      {storeHoursSummary.map(([messageId, abbreviatedDay]) => {
        const openingHours = hours[`${abbreviatedDay}Open`];
        const closingHours = hours[`${abbreviatedDay}Close`];
        return (
          <StoreHourContainer key={messageId}>
            <InnerText>{formatMessage({ id: messageId })}</InnerText>
            <InnerText>{hoursOfOperation(openingHours, closingHours)}</InnerText>
          </StoreHourContainer>
        );
      })}
    </Box>
  );
};

export const StoreInfoModal = ({ store }: IStoreInfoModalProps) => {
  const {
    name: storeName,
    phoneNumber,
    restaurantImage,
    latitude: lat,
    longitude: lng,
    physicalAddress,
  } = store;
  const { StoreChangeDialog } = useStoreContext();
  const nonTraditionalStoreUI = useNonTraditionalStoreUI({ restaurant: store });
  const router = useRouter();

  const { current: position } = useRef({ lat, lng });
  const { activeCoordinates } = useGeolocation();
  const { formatMessage } = useIntl();
  const {
    fetchingPosData,
    reorder: { reordering },
  } = useOrderContext();

  const isOrderingEnabled = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);

  const { selectRestaurant } = useSelectStore({ restaurant: store });

  const streetAddress = physicalAddress?.address1;
  const formattedCity = physicalAddress?.city;
  const state = getUSState(physicalAddress?.stateProvince ?? '');
  const postalCode = physicalAddress?.postalCode?.split('-')[0];
  const {
    isOpenForDelivery,
    isOpenForPickup,
    getStoreServiceModeStatuses,
    isMobileOrderingAvailable,
    isStoreAvailableForPickup,
    isServiceModeCapable,
  } = useStoreAvailabilityFns();
  const shouldShowWaysToOrderSection =
    isMobileOrderingAvailable(store) || isOpenForDelivery(store) || isOpenForPickup(store);

  const canonicalUrl = [`${routes.store}`, getStoreSEOSlug(store)].join('/');
  const serviceModeStatuses = getStoreServiceModeStatuses(store);
  const storeLocalTime = getStoreLocalTime();

  const shouldShowDineIn = isServiceModeCapable(store, ServiceMode.EAT_IN, storeLocalTime);
  const shouldShowTakeout = isServiceModeCapable(store, ServiceMode.TAKEOUT, storeLocalTime);
  const shouldShowMobileOrderDriveThru = isServiceModeCapable(
    store,
    ServiceMode.MOBILE_ORDER_DRIVE_THRU,
    storeLocalTime
  );
  const shouldShowDriveThru = isServiceModeCapable(store, ServiceMode.DRIVE_THRU, storeLocalTime);
  const shouldShowCurbside = isServiceModeCapable(store, ServiceMode.CURBSIDE, storeLocalTime);
  const isDeliveryAvailable = !!serviceModeStatuses.find(
    sms => sms.serviceMode === ServiceMode.DELIVERY
  )?.available;

  const amenitiesBreakdown: [IconVariant, boolean, string, string][] = [
    ['mobile', useIsMobileOrderingAvailable(store), '22px', 'mobileOrderingAndPickup'],
    ['delivery', isDeliveryAvailable, '22px', 'delivery'],
  ];

  const storeHoursBreakdown = [
    [shouldShowDineIn || shouldShowTakeout, 'dineInHours', 'diningRoomHours'],
    [shouldShowDriveThru, 'driveThruHours'],
    [shouldShowMobileOrderDriveThru, 'mobileOrderDriveThruHours'],
    [shouldShowCurbside, 'curbsideHours'],
  ];

  const orderButtonEnabled = isStoreAvailableForPickup(store) && !fetchingPosData;

  const amenitiesSection = useMemo(() => {
    if (!store.amenities?.length) {
      return null;
    }

    const amenitiesWithIconSrc = store.amenities.filter(amenity => amenity?.icon?.asset?.url);

    if (!amenitiesWithIconSrc.length) {
      return null;
    }

    return (
      <VStack testID="restaurant-amenities">
        <InnerHeader accessibilityLevel={2} variant="headerThree" testID="in-store-amenities">
          {formatMessage({ id: 'inStoreAmenities' })}
        </InnerHeader>
        {amenitiesWithIconSrc.map(amenity => {
          const name = amenity?.name?.en ?? '';
          return (
            <Amenity key={name}>
              <FeatureIcon src={amenity?.icon?.asset?.url ?? undefined} alt={name} />
              <InnerText>{name}</InnerText>
            </Amenity>
          );
        })}
      </VStack>
    );
  }, [formatMessage, store]);

  const handleOnSelectStore = useCallback(async () => {
    if (nonTraditionalStoreUI?.isNonTraditionalStore) {
      nonTraditionalStoreUI.openDrawer();
      return;
    }
    await selectRestaurant();
  }, [nonTraditionalStoreUI, selectRestaurant]);

  const handleOnConfirmNonTraditionalStoreDialog = async () => {
    await selectRestaurant();
    nonTraditionalStoreUI?.closeDrawer();
  };

  // Loading state covers two scenarios:
  // - fetchingPosData: Loading store POS connectivity/menu data for regular ordering
  // - reordering: Processing a reorder request (users may be redirected here to select a store if no store selected)
  const isLoading = fetchingPosData || reordering;

  return (
    <CardModal
      testID="store-info-modal"
      analyticsEvent="Store Details"
      scrollView
      standardPadding
      _headerOverride={
        <>
          <View style={styles.closeButton}>
            <CloseButton
              accessible
              accessibilityRole="button"
              accessibilityLabel={formatMessage({ id: 'closeRestaurantInfoAccessibilityLabel' })}
              onPress={() => router.dismiss()}
            />
          </View>
          <HeaderContainer>
            {restaurantImage ? (
              <Picture image={restaurantImage} alt={storeName ?? ''} />
            ) : (
              <StaticMap
                initialZoomIOS={8000}
                position={position as ILocation}
                title={storeName ?? physicalAddress?.address1 ?? undefined}
              />
            )}
          </HeaderContainer>
        </>
      }
      footer={
        isOrderingEnabled && (
          <ActionButton
            isLoading={isLoading}
            testID="store-modal-order-here"
            fullWidth
            disabled={!orderButtonEnabled}
            onPress={handleOnSelectStore}
          >
            {formatMessage({ id: 'orderHere' })}
          </ActionButton>
        )
      }
    >
      {/* @ts-ignore */}
      <Helmet testID="canonical" link={[{ rel: 'canonical', href: canonicalUrl }]} />
      <InnerHeader
        accessibilityLevel={1}
        variant="headerTwo"
        testID="address"
        nativeID="modal-heading"
      >
        {streetAddress?.toLowerCase()}
      </InnerHeader>
      <Box>
        <DirectionsLink activeCoordinates={activeCoordinates} mapPosition={position as ILocation} />
        <Address>{`${formattedCity}${state ? ` ${state}` : ''}, ${postalCode}`}</Address>
        {phoneNumber && (
          <PhoneNumber
            accessibilityLabel={`${formatMessage({ id: 'phoneNumber' })} ${phoneNumber}`}
            testID="phoneNumber"
            to={`tel:${phoneNumber}`}
          >
            {phoneNumber}
          </PhoneNumber>
        )}
      </Box>
      <SpacingLine marginTop="$6" bottom="$3" />
      {isOrderingEnabled && (
        <>
          {shouldShowWaysToOrderSection && (
            <InnerHeader accessibilityLevel={2} variant="headerThree">
              {formatMessage({ id: 'waysToOrder' })}
            </InnerHeader>
          )}
          {amenitiesBreakdown.map(
            ([icon, shouldShow, height, messageId]) =>
              shouldShow && (
                <Amenity key={messageId}>
                  <AmenityIcon
                    variant={icon}
                    width="40px"
                    height={height}
                    {...hiddenAccessibilityPlatformProps}
                  />
                  <InnerText>{formatMessage({ id: messageId })}</InnerText>
                </Amenity>
              )
          )}
          {shouldShowWaysToOrderSection && <SpacingLine marginTop="$1" marginBottom="$4" />}
        </>
      )}

      {amenitiesSection}
      {storeHoursBreakdown.map(
        // @ts-expect-error TS(2345) FIXME: Argument of type '([shouldShow, messageId, hours]:... Remove this comment to see the full error message
        ([shouldShow, messageId, hours = messageId]: [boolean, string, string]) =>
          shouldShow && (
            <Hours key={messageId} title={formatMessage({ id: messageId })} hours={store[hours]} />
          )
      )}
      <StoreMetaForSEO
        atCopy={formatMessage({ id: 'at' })}
        streetAddress={streetAddress ?? ''}
        city={formattedCity ?? ''}
        state={state}
        postalCode={postalCode ?? ''}
        phoneNumber={phoneNumber ?? ''}
        image={(restaurantImage as IImage) ?? undefined}
      />
      <StoreChangeDialog heading={formatMessage({ id: 'changeStores' })} />
      {nonTraditionalStoreUI && (
        <NonTraditionalStoreDialogDrawer
          onConfirm={handleOnConfirmNonTraditionalStoreDialog}
          onCancel={nonTraditionalStoreUI.closeDrawer}
          isOpen={nonTraditionalStoreUI.isDialogDrawerOpen}
          accessInstructions={nonTraditionalStoreUI.accessInstructions}
        />
      )}
    </CardModal>
  );
};

const StoreInfoModalContainer = () => {
  const { id } = useLocalSearchParams<{ id?: string }>();

  const [ErrorDialog] = useErrorModal({
    init: true,
    modalAppearanceEventMessage: 'Error: Fetching Restaurant Info Failure',
  });
  const getRestaurantFromSanity = useGetRestaurantsQuery({
    skip: !id,
    variables: {
      filter: {
        _id: id,
      },
      limit: 1,
    },
  });
  const secondQueryVariables = useMemo(
    () => ({
      storeId: getRestaurantFromSanity?.data?.allRestaurants?.[0]?.number,
    }),
    [getRestaurantFromSanity]
  );
  const getRestaurantFromRBI = useGetRestaurantQuery({
    fetchPolicy: 'cache-and-network',
    skip: !secondQueryVariables.storeId,
    variables: secondQueryVariables,
  });

  const { restaurant, error, loading } = useMemo(
    () => ({
      restaurant:
        getRestaurantFromRBI.data &&
        getRestaurantFromSanity.data &&
        getRestaurantFromSanity.data.allRestaurants.length !== 0
          ? mergeRestaurantData({
              rbiRestaurant: (getRestaurantFromRBI.data.restaurant as unknown) as IRestaurant,
              sanityStore: getRestaurantFromSanity.data.allRestaurants[0] as any,
            })
          : undefined,
      error: getRestaurantFromRBI.error || getRestaurantFromSanity.error || undefined,
      loading: getRestaurantFromRBI.loading || getRestaurantFromSanity.loading,
    }),
    [getRestaurantFromRBI, getRestaurantFromSanity]
  );

  if (error) {
    return <ErrorDialog />;
  }

  const isLoading = loading || !restaurant;

  if (isLoading) {
    return (
      <CardModal>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return <StoreInfoModal store={restaurant} />;
};

const styles = StyleSheet.create({
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
    backgroundColor: primitive.$white,
    borderRadius: 20,
  },
});

export default StoreInfoModalContainer;
